// src/features/activity/RecordDecisionDialog.tsx
// Record a team decision — the one write path on ActivityService besides
// Kafka consumers. Creates an Activity with category=DECISION.

import { useState } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { activityDecisionStore } from '@core/stores';
import { Button, Input, Textarea, ErrorAlert } from '@react/components';
import { Select } from '@/components/Select';
import { useScope } from '@/hooks/useScope';
import { ActivityModal } from './Modal';
import { makeEntity, errMessage } from './logic';

interface RecordDecisionDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export const RecordDecisionDialog = observer(({ open, onClose, onSaved }: RecordDecisionDialogProps) => {
  const { t } = useI18n();
  const scope = useScope();
  const [objectType, setObjectType] = useState('task');
  const [objectId, setObjectId] = useState('');
  const [summary, setSummary] = useState('');
  const [details, setDetails] = useState('');
  const [decisionScope, setDecisionScope] = useState<'ACTIVITY_SCOPE_PROJECT' | 'ACTIVITY_SCOPE_TEAM'>('ACTIVITY_SCOPE_PROJECT');
  const [scopeId, setScopeId] = useState('');
  const [notify, setNotify] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setObjectType('task'); setObjectId(''); setSummary(''); setDetails('');
    setDecisionScope('ACTIVITY_SCOPE_PROJECT'); setScopeId(''); setNotify(''); setError(null);
  };

  const submit = async () => {
    if (!summary.trim() || !objectId.trim() || !scopeId.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await activityDecisionStore.RecordDecision({
        object: makeEntity(objectId.trim(), objectType.trim() || 'task', scope),
        summary: summary.trim(),
        details: details.trim() ? { rationale: details.trim() } : {},
        scope: decisionScope,
        scope_id: scopeId.trim(),
        notify_scope: notify.trim() || undefined,
      });
      reset();
      onSaved();
      onClose();
    } catch (e) {
      setError(errMessage(e, t('activity.decisions.form.error')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ActivityModal
      open={open}
      title={t('activity.decisions.form.title')}
      onClose={() => { reset(); onClose(); }}
      footer={
        <>
          <Button variant="secondary" onClick={() => { reset(); onClose(); }}>{t('common.cancel')}</Button>
          <Button onClick={submit} isLoading={saving} disabled={!summary.trim() || !objectId.trim() || !scopeId.trim()}>
            {t('activity.decisions.form.submit')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error && <ErrorAlert error={error} />}
        <div className="grid grid-cols-2 gap-2">
          <Input label={t('activity.decisions.form.objectType')} value={objectType} onChange={(e) => setObjectType(e.target.value)} />
          <Input label={t('activity.decisions.form.objectId')} value={objectId} onChange={(e) => setObjectId(e.target.value)} required />
        </div>
        <Input
          label={t('activity.decisions.form.summary')}
          placeholder={t('activity.decisions.form.summaryPlaceholder')}
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          required
        />
        <Textarea
          label={t('activity.decisions.form.details')}
          placeholder={t('activity.decisions.form.detailsPlaceholder')}
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          rows={3}
        />
        <div className="grid grid-cols-2 gap-2">
          <Select
            label={t('activity.decisions.form.scope')}
            value={decisionScope}
            onChange={(e) => setDecisionScope(e.target.value as 'ACTIVITY_SCOPE_PROJECT' | 'ACTIVITY_SCOPE_TEAM')}
            options={[
              { value: 'ACTIVITY_SCOPE_PROJECT', label: t('activity.feed.scope.project') },
              { value: 'ACTIVITY_SCOPE_TEAM', label: t('activity.feed.scope.team') },
            ]}
          />
          <Input label={t('activity.feed.scopeId')} value={scopeId} onChange={(e) => setScopeId(e.target.value)} required />
        </div>
        <Input
          label={t('activity.decisions.form.notify')}
          placeholder={t('activity.decisions.form.notifyPlaceholder')}
          value={notify}
          onChange={(e) => setNotify(e.target.value)}
        />
      </div>
    </ActivityModal>
  );
});

export default RecordDecisionDialog;
